import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';
import { ASSESSMENT_TYPE_DESCRIPTIONS } from '../../utils/hiraConstants';
import HazardAutocomplete from '../../components/HazardAutocomplete';

const HIRADetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showHazardModal, setShowHazardModal] = useState(false);
  const [editingHazard, setEditingHazard] = useState(null);

  // Form state for assessment editing
  const [formData, setFormData] = useState({});
  
  // Hazard form state
  const [hazardForm, setHazardForm] = useState({
    hazard_description: '',
    who_at_risk: '',
    severity: 3,
    likelihood: 3,
    existing_controls: [],
    control_measures: []
  });

  const [newControl, setNewControl] = useState({
    control_type: 'Engineering',
    description: '',
    responsibility: '',
    timeline: '',
    status: 'Pending'
  });

  const [existingControlInput, setExistingControlInput] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  
  // New state for enhanced features
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showCAPAModal, setShowCAPAModal] = useState(false);
  const [showJSAModal, setShowJSAModal] = useState(false);
  const [editingJSAStep, setEditingJSAStep] = useState(null);
  const [hazardLibrary, setHazardLibrary] = useState([]);
  const [selectedLibraryHazard, setSelectedLibraryHazard] = useState('');
  const [hazardSearchInput, setHazardSearchInput] = useState('');
  const [capaForm, setCAPAForm] = useState({
    hazard_id: '',
    corrective_action: '',
    preventive_action: '',
    responsible_person: user?.name || '',
    due_date: ''
  });
  
  // JSA Step form state
  const [jsaStepForm, setJsaStepForm] = useState({
    step_sequence: 1,
    step_description: '',
    hazard_description: '',
    existing_controls: [],
    recommended_controls: []
  });
  
  const [jsaControlInput, setJsaControlInput] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'mom', or 'audit'

  // MOM (Minutes of Meeting) form state
  const [momFormData, setMomFormData] = useState({
    meeting_date: '',
    meeting_time: '',
    meeting_location: '',
    facilitator_name: '',
    attendees: [],
    previous_minutes_approved: false,
    previous_minutes_notes: '',
    previous_actions: []
  });
  const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false);
  const [newAttendee, setNewAttendee] = useState({ name: '', department: '', role: '' });
  const [showAddActionModal, setShowAddActionModal] = useState(false);
  const [newAction, setNewAction] = useState({
    action_description: '',
    assigned_to: '',
    due_date: '',
    status: 'Pending',
    review_notes: ''
  });

  useEffect(() => {
    fetchAssessment();
    fetchHazardLibrary();
  }, [id]);

  const fetchHazardLibrary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hazard-library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHazardLibrary(response.data);
    } catch (err) {
      console.error('Failed to fetch hazard library:', err);
    }
  };

  const fetchAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessment(response.data);
      setFormData({
        title: response.data.title,
        description: response.data.description || '',
        assessment_type: response.data.assessment_type,
        department: response.data.department || '',
        location: response.data.location || '',
        status: response.data.status,
        review_date: response.data.review_date ? response.data.review_date.split('T')[0] : ''
      });
      // Load MOM data if available
      const momInfo = response.data.mom_meeting_info || {};
      setMomFormData({
        meeting_date: momInfo.meeting_date || '',
        meeting_time: momInfo.meeting_time || '',
        meeting_location: momInfo.meeting_location || response.data.location || '',
        facilitator_name: momInfo.facilitator_name || response.data.assessor_name || '',
        attendees: momInfo.attendees || [],
        previous_minutes_approved: momInfo.previous_minutes_approved || false,
        previous_minutes_notes: momInfo.previous_minutes_notes || '',
        previous_actions: momInfo.previous_actions || []
      });
    } catch (err) {
      console.error('Failed to fetch assessment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditing(false);
      fetchAssessment();
    } catch (err) {
      console.error('Failed to update assessment:', err);
      showError('Failed to update assessment');
    }
  };

  const handleDeleteAssessment = async () => {
    const confirmed = await confirm('Are you sure you want to delete this risk assessment?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/risk-assessments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/hira');
    } catch (err) {
      console.error('Failed to delete assessment:', err);
      showError('Failed to delete assessment');
    }
  };

  const calculateRiskScore = (severity, likelihood) => severity * likelihood;

  const calculateRiskLevel = (score) => {
    if (score <= 5) return 'Low';
    if (score <= 12) return 'Medium';
    if (score <= 20) return 'High';
    return 'Critical';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Critical': return 'bg-red-600 text-white';
      case 'High': return 'bg-orange-500 text-white';
      case 'Medium': return 'bg-yellow-500 text-white';
      case 'Low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const openHazardModal = (hazard = null) => {
    if (hazard) {
      setEditingHazard(hazard);
      setHazardForm({
        hazard_description: hazard.hazard_description,
        who_at_risk: hazard.who_at_risk || '',
        severity: hazard.severity,
        likelihood: hazard.likelihood,
        existing_controls: hazard.existing_controls || [],
        control_measures: hazard.control_measures || [],
        residual_severity: hazard.residual_severity || null,
        residual_likelihood: hazard.residual_likelihood || null
      });
    } else {
      setEditingHazard(null);
      setHazardForm({
        hazard_description: '',
        who_at_risk: '',
        severity: 3,
        likelihood: 3,
        existing_controls: [],
        control_measures: [],
        residual_severity: null,
        residual_likelihood: null
      });
    }
    // Reset AI suggestions and library selection
    setAiSuggestions([]);
    setSelectedLibraryHazard('');
    setHazardSearchInput('');
    setShowHazardModal(true);
  };

  const handleSaveHazard = async () => {
    const riskScore = calculateRiskScore(hazardForm.severity, hazardForm.likelihood);
    const riskLevel = calculateRiskLevel(riskScore);

    const hazardData = {
      ...hazardForm,
      risk_score: riskScore,
      risk_level: riskLevel
    };

    // Calculate residual risk if provided
    if (hazardForm.residual_severity && hazardForm.residual_likelihood) {
      hazardData.residual_risk_score = calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood);
      hazardData.residual_risk_level = calculateRiskLevel(hazardData.residual_risk_score);
    }

    let updatedHazards;
    if (editingHazard) {
      // Update existing hazard
      updatedHazards = assessment.hazards.map(h =>
        h.id === editingHazard.id ? { ...editingHazard, ...hazardData } : h
      );
    } else {
      // Add new hazard
      updatedHazards = [...(assessment.hazards || []), hazardData];
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { hazards: updatedHazards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowHazardModal(false);
      fetchAssessment();
    } catch (err) {
      console.error('Failed to save hazard:', err);
      showError('Failed to save hazard');
    }
  };

  const handleDeleteHazard = async (hazardId) => {
    const confirmed = await confirm('Are you sure you want to delete this hazard?');
    if (!confirmed) return;

    const updatedHazards = assessment.hazards.filter(h => h.id !== hazardId);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { hazards: updatedHazards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAssessment();
    } catch (err) {
      console.error('Failed to delete hazard:', err);
      showError('Failed to delete hazard');
    }
  };

  const addExistingControl = () => {
    if (existingControlInput.trim()) {
      setHazardForm(prev => ({
        ...prev,
        existing_controls: [...prev.existing_controls, existingControlInput.trim()]
      }));
      setExistingControlInput('');
    }
  };

  const removeExistingControl = (index) => {
    setHazardForm(prev => ({
      ...prev,
      existing_controls: prev.existing_controls.filter((_, i) => i !== index)
    }));
  };

  const addControlMeasure = () => {
    if (newControl.description.trim()) {
      setHazardForm(prev => ({
        ...prev,
        control_measures: [...prev.control_measures, { ...newControl }]
      }));
      setNewControl({
        control_type: 'Engineering',
        description: '',
        responsibility: '',
        timeline: '',
        status: 'Pending'
      });
    }
  };

  const removeControlMeasure = (index) => {
    setHazardForm(prev => ({
      ...prev,
      control_measures: prev.control_measures.filter((_, i) => i !== index)
    }));
  };

  const fetchAISuggestions = async () => {
    if (!hazardForm.hazard_description.trim()) {
      showError('Please enter a hazard description first');
      return;
    }

    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/suggest-controls`,
        {
          hazard_description: hazardForm.hazard_description,
          severity: hazardForm.severity,
          likelihood: hazardForm.likelihood,
          who_at_risk: hazardForm.who_at_risk
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch AI suggestions:', err);
      showError('Failed to get AI suggestions. Please try again.');
    } finally {
      setLoadingAI(false);
    }
  };

  const addAISuggestion = (suggestion) => {
    setHazardForm(prev => ({
      ...prev,
      control_measures: [
        ...prev.control_measures,
        {
          control_type: suggestion.control_type,
          description: suggestion.description,
          responsibility: '',
          timeline: '',
          status: 'Pending'
        }
      ]
    }));
    // Remove the suggestion from the list
    setAiSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  // Approval workflow handlers
  const handleApprovalAction = async (action, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/approval-action`,
        { action, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(response.data.message);
      fetchAssessment();
    } catch (err) {
      showError(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleCreateVersion = async (changeType, notes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/create-version?change_type=${changeType}&change_notes=${notes}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('New version created successfully');
      navigate(`/hira/${response.data.new_id}`);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to create version');
    }
  };

  const handleExportMOM = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/risk-assessments/${id}/export-mom`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MOM_Risk_Assessment_${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showError('Failed to export MOM report');
    }
  };

  const handleSaveMOM = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { mom_meeting_info: momFormData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('MOM information saved successfully');
      fetchAssessment();
    } catch (err) {
      console.error('Failed to save MOM:', err);
      showError('Failed to save MOM information');
    }
  };

  const handleAddAttendee = () => {
    if (!newAttendee.name.trim()) {
      showError('Attendee name is required');
      return;
    }
    setMomFormData(prev => ({
      ...prev,
      attendees: [...prev.attendees, { ...newAttendee, id: Date.now().toString() }]
    }));
    setNewAttendee({ name: '', department: '', role: '' });
    setShowAddAttendeeModal(false);
  };

  const handleRemoveAttendee = (attendeeId) => {
    setMomFormData(prev => ({
      ...prev,
      attendees: prev.attendees.filter(a => a.id !== attendeeId)
    }));
  };

  const handleAddPreviousAction = () => {
    if (!newAction.action_description.trim()) {
      showError('Action description is required');
      return;
    }
    setMomFormData(prev => ({
      ...prev,
      previous_actions: [...prev.previous_actions, { ...newAction, id: Date.now().toString() }]
    }));
    setNewAction({
      action_description: '',
      assigned_to: '',
      due_date: '',
      status: 'Pending',
      review_notes: ''
    });
    setShowAddActionModal(false);
  };

  const handleRemovePreviousAction = (actionId) => {
    setMomFormData(prev => ({
      ...prev,
      previous_actions: prev.previous_actions.filter(a => a.id !== actionId)
    }));
  };

  const handleUpdateActionStatus = (actionId, newStatus) => {
    setMomFormData(prev => ({
      ...prev,
      previous_actions: prev.previous_actions.map(a =>
        a.id === actionId ? { ...a, status: newStatus } : a
      )
    }));
  };

  const handleCreateCAPA = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/risk-assessments/${id}/create-capa`,
        capaForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('CAPA created and linked successfully');
      setShowCAPAModal(false);
      setCAPAForm({
        hazard_id: '',
        corrective_action: '',
        preventive_action: '',
        responsible_person: user?.name || '',
        due_date: ''
      });
      fetchAssessment();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to create CAPA');
    }
  };

  const openCAPAModal = (hazard, index) => {
    setCAPAForm({
      hazard_id: hazard.id || String(index),
      corrective_action: '',
      preventive_action: '',
      responsible_person: user?.name || '',
      due_date: ''
    });
    setShowCAPAModal(true);
  };

  const handleLibrarySelect = async (selected) => {
    // Handle selection from the autocomplete component
    if (selected) {
      setSelectedLibraryHazard(selected.id);
      setHazardSearchInput(selected.hazard_name || '');

      // Check if it's a custom hazard created by the user
      if (selected.isCustom) {
        // For custom hazards, just set the description
        setHazardForm(prev => ({
          ...prev,
          hazard_description: selected.hazard_description || selected.hazard_name,
          // Keep existing severity and controls for custom hazards
        }));

        // Ask if user wants to save custom hazard to library for future use
        const shouldSave = await confirm(
          `Would you like to save "${selected.hazard_name}" to the hazard library for future use?`
        );

        if (shouldSave) {
          try {
            const token = localStorage.getItem('token');
            await axios.post(
              `${API}/hazard-library/custom`,
              {
                hazard_name: selected.hazard_name,
                hazard_description: selected.hazard_description || selected.hazard_name,
                category: 'Custom',
                severity_guidance: hazardForm.severity
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            showSuccess('Custom hazard saved to library for future use');
            // Refresh hazard library
            fetchHazardLibrary();
          } catch (err) {
            // Don't show error if hazard already exists
            if (!err.response?.data?.detail?.includes('already exists')) {
              console.error('Failed to save custom hazard:', err);
            }
          }
        }
      } else {
        // For library hazards, auto-fill all fields
        setHazardForm(prev => ({
          ...prev,
          hazard_description: selected.hazard_description,
          severity: selected.severity_guidance || prev.severity,
          existing_controls: [],
          control_measures: (selected.suggested_controls || []).map(ctrl => ({
            control_type: 'Engineering',
            description: ctrl,
            responsibility: '',
            timeline: '',
            status: 'Pending'
          }))
        }));
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-gray-100 text-gray-700';
      case 'Submitted_for_Review': return 'bg-blue-100 text-blue-700';
      case 'Reviewed_Return_to_Draft': return 'bg-yellow-100 text-yellow-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Archived': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // JSA Step handlers
  const openJSAModal = (step = null) => {
    if (step) {
      setEditingJSAStep(step);
      setJsaStepForm({
        step_sequence: step.step_sequence,
        step_description: step.step_description,
        hazard_description: step.hazard_description,
        existing_controls: step.existing_controls || [],
        recommended_controls: step.recommended_controls || []
      });
    } else {
      const nextSeq = (assessment.jsa_steps?.length || 0) + 1;
      setEditingJSAStep(null);
      setJsaStepForm({
        step_sequence: nextSeq,
        step_description: '',
        hazard_description: '',
        existing_controls: [],
        recommended_controls: []
      });
    }
    setJsaControlInput('');
    setShowJSAModal(true);
  };

  const handleSaveJSAStep = async () => {
    if (!jsaStepForm.step_description || !jsaStepForm.hazard_description) {
      showError('Please fill in step description and hazard description');
      return;
    }

    const stepData = {
      id: editingJSAStep?.id || `jsa-${Date.now()}`,
      ...jsaStepForm
    };

    let updatedSteps;
    if (editingJSAStep) {
      updatedSteps = assessment.jsa_steps.map(s =>
        s.id === editingJSAStep.id ? stepData : s
      );
    } else {
      updatedSteps = [...(assessment.jsa_steps || []), stepData];
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { jsa_steps: updatedSteps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowJSAModal(false);
      fetchAssessment();
    } catch (err) {
      showError('Failed to save JSA step');
    }
  };

  const handleDeleteJSAStep = async (stepId) => {
    const confirmed = await confirm('Delete this JSA step?');
    if (!confirmed) return;

    const updatedSteps = assessment.jsa_steps.filter(s => s.id !== stepId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { jsa_steps: updatedSteps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAssessment();
    } catch (err) {
      showError('Failed to delete JSA step');
    }
  };

  const addJSAControl = () => {
    if (jsaControlInput.trim()) {
      setJsaStepForm(prev => ({
        ...prev,
        existing_controls: [...prev.existing_controls, jsaControlInput.trim()]
      }));
      setJsaControlInput('');
    }
  };

  const removeJSAControl = (index) => {
    setJsaStepForm(prev => ({
      ...prev,
      existing_controls: prev.existing_controls.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Assessment not found</p>
        </div>
      </div>
      </div>
    );
  }

  const isApproved = assessment.status === 'Approved';
  const canApprove = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager';
  const isExpiringSoon = assessment.revalidation_required;

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hira')}
            className="text-[#1B5C94] hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Risk Assessments
          </button>

          {isExpiringSoon && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700">⚠️ <strong>Revalidation Required:</strong> This assessment expires soon. Please review and update.</p>
            </div>
          )}

          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {editing ? (
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="text-3xl font-bold text-gray-800 border-b-2 border-[#1B5C94] focus:outline-none flex-1"
                  />
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-gray-800">{assessment.title}</h1>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(assessment.status)}`}>
                      {assessment.status.replace(/_/g, ' ')}
                    </span>
                    <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">
                      v{assessment.version_number}
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-4 mt-2 text-sm text-gray-600">
                <span>Created: {new Date(assessment.created_at).toLocaleDateString()}</span>
                <span>Assessor: {assessment.assessor_name}</span>
                {assessment.validity_expiry_date && (
                  <span>Expires: {new Date(assessment.validity_expiry_date).toLocaleDateString()}</span>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleExportMOM}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                title="Export for Singapore MOM"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export MOM
              </button>

              {!isApproved && (
                <button
                  onClick={() => {
                    if (!assessment.hazards || assessment.hazards.length === 0) {
                      showError('Please add at least one hazard before creating a CAPA');
                      return;
                    }
                    setShowCAPAModal(true);
                  }}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    assessment.hazards && assessment.hazards.length > 0
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-orange-300 text-white cursor-not-allowed'
                  }`}
                  title={!assessment.hazards || assessment.hazards.length === 0 ? 'Add hazards first to create CAPA' : 'Create CAPA from hazard'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Create CAPA
                </button>
              )}

              {isApproved && canApprove && (
                <button
                  onClick={() => setShowVersionModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  New Version
                </button>
              )}

              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAssessment}
                    className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  {!isApproved && (
                    <button
                      onClick={() => setEditing(true)}
                      className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                    >
                      Edit
                    </button>
                  )}
                  {canApprove && (
                    <button
                      onClick={handleDeleteAssessment}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Delete
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('mom')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mom'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meeting Info
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Approval Workflow Buttons */}
        {!isApproved && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Workflow Actions</h3>
            <div className="flex gap-3">
              {(assessment.status === 'Draft' || assessment.status === 'Reviewed_Return_to_Draft') && (
                <button
                  onClick={() => handleApprovalAction('submit')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  📤 Submit for Review
                </button>
              )}

              {canApprove && assessment.status === 'Submitted_for_Review' && (
                <>
                  <button
                    onClick={() => handleApprovalAction('approve')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    ✅ Approve
                  </button>
                  <button
                    onClick={() => {
                      const notes = prompt('Enter review notes:');
                      if (notes) handleApprovalAction('return_to_draft', notes);
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    🔄 Return to Draft
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Assessment Details Card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Assessment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Type</label>
              {editing ? (
                <>
                <select
                  value={formData.assessment_type}
                  onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Activity-based">Activity-based</option>
                  <option value="Task-based">Task-based</option>
                  <option value="JSA">Job Safety Analysis (JSA)</option>
                  <option value="General">General Assessment</option>
                </select>
                {/* Description text */}
                {formData.assessment_type && (
                  <p className="text-xs text-gray-600 mt-2">
                    {ASSESSMENT_TYPE_DESCRIPTIONS[formData.assessment_type]}
                  </p>
                )}
                </>
              ) : (
                <p className="text-gray-800">
                  {assessment.assessment_type}
                  {assessment.assessment_type === 'JSA' && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Step-based workflow enabled</span>
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              {editing ? (
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Archived">Archived</option>
                </select>
              ) : (
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  assessment.status === 'Active' ? 'bg-green-100 text-green-700' :
                  assessment.status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                  assessment.status === 'Under Review' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {assessment.status}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">{assessment.department || 'Not specified'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">{assessment.location || 'Not specified'}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              {editing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">{assessment.description || 'No description provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Review Date</label>
              {editing ? (
                <input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">
                  {assessment.review_date
                    ? new Date(assessment.review_date).toLocaleDateString()
                    : 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* JSA Steps Section - Only for JSA type assessments */}
        {assessment.assessment_type === 'JSA' && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">📋 Job Safety Analysis - Steps</h2>
              {!isApproved && (
                <button
                  onClick={() => openJSAModal()}
                  className="bg-[#1B5C94] text-white px-4 py-2 rounded-lg hover:bg-[#164A7B]"
                >
                  Add JSA Step
                </button>
              )}
            </div>

            {assessment.jsa_steps && assessment.jsa_steps.length > 0 ? (
              <div className="space-y-3">
                {assessment.jsa_steps.sort((a, b) => a.step_sequence - b.step_sequence).map((step) => (
                  <div key={step.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-gray-800">Step {step.step_sequence}: {step.step_description}</h3>
                      {!isApproved && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openJSAModal(step)}
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteJSAStep(step.id)}
                            className="text-red-600 hover:underline text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mb-2"><strong>Hazard:</strong> {step.hazard_description}</p>
                    {step.existing_controls && step.existing_controls.length > 0 && (
                      <p className="text-sm text-gray-600"><strong>Existing Controls:</strong> {step.existing_controls.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-blue-50 rounded-lg">
                <p className="text-gray-600 mb-2">No JSA steps added yet</p>
                <p className="text-sm text-gray-500">For JSA assessments, break down the job into sequential steps and identify hazards for each step.</p>
              </div>
            )}
          </div>
        )}

        {/* Hazards Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Identified Hazards & Risk Matrix</h2>
            <button
              onClick={() => openHazardModal()}
              className="bg-[#1B5C94] text-white px-4 py-2 rounded-lg hover:bg-[#164A7B] flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Hazard
            </button>
          </div>

          {assessment.hazards && assessment.hazards.length > 0 ? (
            <div className="space-y-4">
              {assessment.hazards.map((hazard, index) => (
                <div key={hazard.id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800">{hazard.hazard_description}</h3>
                      {hazard.who_at_risk && (
                        <p className="text-sm text-gray-600 mt-1">At Risk: {hazard.who_at_risk}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!isApproved && (
                        <button
                          onClick={() => openCAPAModal(hazard, index)}
                          className="text-orange-600 hover:underline text-sm"
                        >
                          Create CAPA
                        </button>
                      )}
                      <button
                        onClick={() => openHazardModal(hazard)}
                        className="text-[#1B5C94] hover:underline text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteHazard(hazard.id)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Initial Risk (Before Controls) */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-600 mb-2 font-semibold">Initial Risk (Before Controls)</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-700">S: {hazard.severity}</span>
                        <span className="text-sm text-gray-700">× L: {hazard.likelihood}</span>
                        <span className="text-sm text-gray-700">= {hazard.risk_score}</span>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(hazard.risk_level)}`}>
                          {hazard.risk_level}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        <p className="font-semibold mb-1">Controls: {hazard.control_measures?.length || 0}</p>
                      </div>
                    </div>

                    {/* Residual Risk (After Controls) */}
                    <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                      <p className="text-xs text-blue-700 mb-2 font-semibold">Residual Risk (After Controls)</p>
                      {hazard.residual_severity && hazard.residual_likelihood ? (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-blue-700">S: {hazard.residual_severity}</span>
                            <span className="text-sm text-blue-700">× L: {hazard.residual_likelihood}</span>
                            <span className="text-sm text-blue-700">= {hazard.residual_risk_score}</span>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(hazard.residual_risk_level)}`}>
                              {hazard.residual_risk_level}
                            </span>
                          </div>
                          {(hazard.residual_risk_level === 'High' || hazard.residual_risk_level === 'Critical') && (
                            <p className="text-xs text-red-600 font-semibold">⚠️ Cannot approve with High/Critical residual risk</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Not calculated - add controls and reassess</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-gray-600">No hazards identified yet</p>
              <button
                onClick={() => openHazardModal()}
                className="mt-4 text-[#1B5C94] hover:underline font-semibold"
              >
                Add your first hazard
              </button>
            </div>
          )}
        </div>
          </>
        ) : activeTab === 'mom' ? (
          /* MOM (Minutes of Meeting) Tab */
          <>
            {/* Meeting Details */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Meeting Details</h2>
                <button
                  onClick={handleSaveMOM}
                  className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                >
                  Save Meeting Info
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Meeting Date</label>
                  <input
                    type="date"
                    value={momFormData.meeting_date}
                    onChange={(e) => setMomFormData({ ...momFormData, meeting_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Meeting Time</label>
                  <input
                    type="time"
                    value={momFormData.meeting_time}
                    onChange={(e) => setMomFormData({ ...momFormData, meeting_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={momFormData.meeting_location}
                    onChange={(e) => setMomFormData({ ...momFormData, meeting_location: e.target.value })}
                    placeholder="e.g., Conference Room A, Site Office"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Facilitator Name</label>
                  <input
                    type="text"
                    value={momFormData.facilitator_name}
                    onChange={(e) => setMomFormData({ ...momFormData, facilitator_name: e.target.value })}
                    placeholder="Name of the meeting facilitator"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              </div>
            </div>

            {/* Attendees Section */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Attendees</h2>
                <button
                  onClick={() => setShowAddAttendeeModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  + Add Attendee
                </button>
              </div>
              {momFormData.attendees.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {momFormData.attendees.map((attendee) => (
                        <tr key={attendee.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{attendee.name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendee.department || 'N/A'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{attendee.role || 'N/A'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleRemoveAttendee(attendee.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No attendees added yet</p>
                  <p className="text-sm text-gray-500 mt-1">Click "Add Attendee" to add meeting participants</p>
                </div>
              )}
            </div>

            {/* Previous Minutes Section */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Previous Minutes Review</h2>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={momFormData.previous_minutes_approved}
                    onChange={(e) => setMomFormData({ ...momFormData, previous_minutes_approved: e.target.checked })}
                    className="w-5 h-5 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94]"
                  />
                  <span className="text-sm font-medium text-gray-700">Previous minutes approved</span>
                </label>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Review Notes</label>
                <textarea
                  value={momFormData.previous_minutes_notes}
                  onChange={(e) => setMomFormData({ ...momFormData, previous_minutes_notes: e.target.value })}
                  rows={3}
                  placeholder="Notes from the review of previous minutes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                />
              </div>

              {/* Actions from Previous Meeting */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Actions from Previous Meeting</h3>
                  <button
                    onClick={() => setShowAddActionModal(true)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    + Add Action
                  </button>
                </div>
                {momFormData.previous_actions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review Notes</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {momFormData.previous_actions.map((action) => (
                          <tr key={action.id}>
                            <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{action.action_description}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{action.assigned_to || 'N/A'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{action.due_date || 'N/A'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <select
                                value={action.status}
                                onChange={(e) => handleUpdateActionStatus(action.id, e.target.value)}
                                className={`px-2 py-1 rounded text-xs font-bold ${
                                  action.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                  action.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}
                              >
                                <option value="Pending">Pending</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{action.review_notes || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleRemovePreviousAction(action.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">No previous actions recorded</p>
                    <p className="text-sm text-gray-500 mt-1">Click "Add Action" to record actions from the previous meeting</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Audit History Tab */
          <div className="bg-white rounded-xl shadow-md p-6">
            <AuditTrailViewer
              entityType="risk_assessment"
              entityId={id}
              title="Risk Assessment Audit History"
            />
          </div>
        )}

        {/* Add Attendee Modal */}
        {showAddAttendeeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Add Attendee</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={newAttendee.name}
                    onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })}
                    placeholder="Attendee name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={newAttendee.department}
                    onChange={(e) => setNewAttendee({ ...newAttendee, department: e.target.value })}
                    placeholder="e.g., Operations, HSE, Maintenance"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                  <input
                    type="text"
                    value={newAttendee.role}
                    onChange={(e) => setNewAttendee({ ...newAttendee, role: e.target.value })}
                    placeholder="e.g., Safety Officer, Dept. Head, Operator"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddAttendeeModal(false);
                    setNewAttendee({ name: '', department: '', role: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAttendee}
                  className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                >
                  Add Attendee
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Previous Action Modal */}
        {showAddActionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Add Previous Action</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Action Description <span className="text-red-500">*</span></label>
                  <textarea
                    value={newAction.action_description}
                    onChange={(e) => setNewAction({ ...newAction, action_description: e.target.value })}
                    rows={3}
                    placeholder="Describe the action item from the previous meeting..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Assigned To</label>
                    <input
                      type="text"
                      value={newAction.assigned_to}
                      onChange={(e) => setNewAction({ ...newAction, assigned_to: e.target.value })}
                      placeholder="Person responsible"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      value={newAction.due_date}
                      onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  <select
                    value={newAction.status}
                    onChange={(e) => setNewAction({ ...newAction, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Review Notes</label>
                  <textarea
                    value={newAction.review_notes}
                    onChange={(e) => setNewAction({ ...newAction, review_notes: e.target.value })}
                    rows={2}
                    placeholder="Notes from reviewing this action..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddActionModal(false);
                    setNewAction({
                      action_description: '',
                      assigned_to: '',
                      due_date: '',
                      status: 'Pending',
                      review_notes: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPreviousAction}
                  className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                >
                  Add Action
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hazard Modal */}
        {showHazardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingHazard ? 'Edit Hazard' : 'Add New Hazard'}
                </h2>
                <button
                  onClick={() => setShowHazardModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Hazard Library Selector with Autocomplete */}
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-green-900 mb-2">
                    📚 Search Hazard Library or Create Custom
                  </label>
                  <HazardAutocomplete
                    hazardLibrary={hazardLibrary}
                    value={hazardSearchInput}
                    onChange={setHazardSearchInput}
                    onSelect={handleLibrarySelect}
                    placeholder="Type to search (e.g., 'Vib' for Vibration)..."
                  />
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-green-700">
                      <span className="font-medium">Search:</span> Type 2-3 characters to filter hazards (e.g., "Vib" shows "Vibration Exposure")
                    </p>
                    <p className="text-xs text-green-700">
                      <span className="font-medium">Custom:</span> Type your own hazard name and press Enter to create a custom hazard
                    </p>
                    <p className="text-xs text-green-700">
                      <span className="font-medium">Auto-fill:</span> Selecting from library auto-fills description and suggested controls
                    </p>
                  </div>
                </div>

                {/* Hazard Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hazard Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={hazardForm.hazard_description}
                    onChange={(e) => setHazardForm({ ...hazardForm, hazard_description: e.target.value })}
                    rows={3}
                    placeholder="Describe the hazard in detail..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>

                {/* Who is at Risk */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Who is at Risk?
                  </label>
                  <input
                    type="text"
                    value={hazardForm.who_at_risk}
                    onChange={(e) => setHazardForm({ ...hazardForm, who_at_risk: e.target.value })}
                    placeholder="e.g., Workers, Contractors, Visitors"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>

                {/* Risk Matrix - 5x5 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-bold text-gray-800 mb-4">Risk Assessment (5×5 Matrix)</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Severity (Consequence) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={hazardForm.severity}
                        onChange={(e) => setHazardForm({ ...hazardForm, severity: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value={1}>1 - Negligible (Minor injury, first aid)</option>
                        <option value={2}>2 - Minor (Medical treatment, no lost time)</option>
                        <option value={3}>3 - Moderate (Lost time injury, hospitalization)</option>
                        <option value={4}>4 - Major (Permanent disability, multiple casualties)</option>
                        <option value={5}>5 - Catastrophic (Fatality, multiple fatalities)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Likelihood (Probability) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={hazardForm.likelihood}
                        onChange={(e) => setHazardForm({ ...hazardForm, likelihood: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value={1}>1 - Rare (May occur in exceptional circumstances)</option>
                        <option value={2}>2 - Unlikely (Could occur, but not expected)</option>
                        <option value={3}>3 - Possible (Might occur at some time)</option>
                        <option value={4}>4 - Likely (Will probably occur)</option>
                        <option value={5}>5 - Almost Certain (Expected to occur)</option>
                      </select>
                    </div>
                  </div>

                  {/* Risk Score Display */}
                  <div className="bg-white rounded-lg p-4 border-2 border-gray-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Risk Score</p>
                        <p className="text-3xl font-bold text-gray-800">
                          {calculateRiskScore(hazardForm.severity, hazardForm.likelihood)}
                        </p>
                      </div>
                      <div>
                        <span className={`px-4 py-2 rounded-lg text-lg font-bold ${
                          getRiskColor(calculateRiskLevel(calculateRiskScore(hazardForm.severity, hazardForm.likelihood)))
                        }`}>
                          {calculateRiskLevel(calculateRiskScore(hazardForm.severity, hazardForm.likelihood))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Existing Controls */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Existing Controls
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={existingControlInput}
                      onChange={(e) => setExistingControlInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExistingControl())}
                      placeholder="Enter existing control measure"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                    <button
                      type="button"
                      onClick={addExistingControl}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {hazardForm.existing_controls.length > 0 && (
                    <div className="space-y-2">
                      {hazardForm.existing_controls.map((ctrl, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{ctrl}</span>
                          <button
                            onClick={() => removeExistingControl(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Residual Risk (After Controls) */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-4">Residual Risk Assessment (After Controls)</h3>
                  <p className="text-sm text-blue-700 mb-4">After implementing control measures, reassess the risk:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-blue-900 mb-2">
                        Residual Severity
                      </label>
                      <select
                        value={hazardForm.residual_severity || ''}
                        onChange={(e) => setHazardForm({ ...hazardForm, residual_severity: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Not assessed yet --</option>
                        <option value={1}>1 - Negligible</option>
                        <option value={2}>2 - Minor</option>
                        <option value={3}>3 - Moderate</option>
                        <option value={4}>4 - Major</option>
                        <option value={5}>5 - Catastrophic</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-blue-900 mb-2">
                        Residual Likelihood
                      </label>
                      <select
                        value={hazardForm.residual_likelihood || ''}
                        onChange={(e) => setHazardForm({ ...hazardForm, residual_likelihood: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Not assessed yet --</option>
                        <option value={1}>1 - Rare</option>
                        <option value={2}>2 - Unlikely</option>
                        <option value={3}>3 - Possible</option>
                        <option value={4}>4 - Likely</option>
                        <option value={5}>5 - Almost Certain</option>
                      </select>
                    </div>
                  </div>

                  {/* Residual Risk Score Display */}
                  {hazardForm.residual_severity && hazardForm.residual_likelihood && (
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-400">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-700">Residual Risk Score</p>
                          <p className="text-3xl font-bold text-blue-900">
                            {calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)}
                          </p>
                        </div>
                        <div>
                          <span className={`px-4 py-2 rounded-lg text-lg font-bold ${
                            getRiskColor(calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)))
                          }`}>
                            {calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood))}
                          </span>
                        </div>
                      </div>
                      {(calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)) === 'High' || 
                        calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)) === 'Critical') && (
                        <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3">
                          <p className="text-sm text-red-700 font-semibold">⚠️ Warning: High/Critical residual risk will prevent approval</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* AI Suggestions */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <h4 className="font-bold text-purple-900">AI-Powered Control Suggestions</h4>
                    </div>
                    <button
                      type="button"
                      onClick={fetchAISuggestions}
                      disabled={loadingAI || !hazardForm.hazard_description.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold"
                    >
                      {loadingAI ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Get AI Suggestions
                        </>
                      )}
                    </button>
                  </div>

                  {aiSuggestions.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-purple-700 mb-2">Click on a suggestion to add it as a control measure:</p>
                      {aiSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => addAISuggestion(suggestion)}
                          className="bg-white p-3 rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-md cursor-pointer transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-lg">
                              {suggestion.control_type === 'Elimination' ? '🚫' :
                               suggestion.control_type === 'Substitution' ? '🔄' :
                               suggestion.control_type === 'Engineering' ? '🔧' :
                               suggestion.control_type === 'Administrative' ? '📋' : '🦺'}
                            </span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-purple-900">{suggestion.control_type}</div>
                              <div className="text-sm text-gray-700">{suggestion.description}</div>
                            </div>
                            <button className="text-purple-600 hover:text-purple-800 text-xs font-semibold">
                              Add →
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-purple-700 text-center py-2">
                      {loadingAI ? 'AI is analyzing the hazard...' : 'Enter hazard details and click "Get AI Suggestions" for intelligent control recommendations'}
                    </p>
                  )}
                </div>

                {/* Control Measures (Hierarchy of Controls) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Control Measures (Hierarchy of Controls)
                  </label>
                  <div className="space-y-3 mb-3">
                    <select
                      value={newControl.control_type}
                      onChange={(e) => setNewControl({ ...newControl, control_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="Elimination">🚫 Elimination (Most Effective)</option>
                      <option value="Substitution">🔄 Substitution</option>
                      <option value="Engineering">🔧 Engineering Controls</option>
                      <option value="Administrative">📋 Administrative Controls</option>
                      <option value="PPE">🦺 PPE (Least Effective)</option>
                    </select>
                    <input
                      type="text"
                      value={newControl.description}
                      onChange={(e) => setNewControl({ ...newControl, description: e.target.value })}
                      placeholder="Control measure description"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newControl.responsibility}
                        onChange={(e) => setNewControl({ ...newControl, responsibility: e.target.value })}
                        placeholder="Responsible person"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                      <input
                        type="text"
                        value={newControl.timeline}
                        onChange={(e) => setNewControl({ ...newControl, timeline: e.target.value })}
                        placeholder="Timeline (e.g., 30 days)"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addControlMeasure}
                      className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add Control Measure
                    </button>
                  </div>

                  {hazardForm.control_measures.length > 0 && (
                    <div className="space-y-2">
                      {hazardForm.control_measures.map((ctrl, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded border border-gray-200">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold text-sm text-[#1B5C94]">{ctrl.control_type}</span>
                            <button
                              onClick={() => removeControlMeasure(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              ×
                            </button>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">{ctrl.description}</p>
                          <div className="flex gap-4 text-xs text-gray-600">
                            {ctrl.responsibility && <span>👤 {ctrl.responsibility}</span>}
                            {ctrl.timeline && <span>📅 {ctrl.timeline}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowHazardModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHazard}
                  disabled={!hazardForm.hazard_description}
                  className="px-6 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B] disabled:bg-gray-400"
                >
                  {editingHazard ? 'Update Hazard' : 'Save Hazard'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Version Modal */}
        {showVersionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Create New Version</h2>
              <p className="text-sm text-gray-600 mb-4">This will archive the current approved version and create a new draft.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Change Type</label>
                  <select
                    id="changeType"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    defaultValue="Minor"
                  >
                    <option value="Minor">Minor (v{assessment.version_number || '1.0'} → v{assessment.version_number ? assessment.version_number.split('.')[0] : '1'}.{assessment.version_number ? parseInt(assessment.version_number.split('.')[1]) + 1 : '1'})</option>
                    <option value="Major">Major (v{assessment.version_number || '1.0'} → v{assessment.version_number ? parseInt(assessment.version_number.split('.')[0]) + 1 : '2'}.0)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Change Notes</label>
                  <textarea
                    id="changeNotes"
                    rows={3}
                    placeholder="Describe what changes will be made..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowVersionModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const changeType = document.getElementById('changeType').value;
                    const notes = document.getElementById('changeNotes').value;
                    handleCreateVersion(changeType, notes);
                    setShowVersionModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Version
                </button>
              </div>
            </div>
          </div>
        )}

        {/* JSA Step Modal */}
        {showJSAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingJSAStep ? 'Edit JSA Step' : 'Add JSA Step'}
                </h2>
                <button
                  onClick={() => setShowJSAModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Step Sequence <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={jsaStepForm.step_sequence}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, step_sequence: parseInt(e.target.value) })}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Step Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={jsaStepForm.step_description}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, step_description: e.target.value })}
                    placeholder="e.g., Climb ladder to access roof"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hazard Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={jsaStepForm.hazard_description}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, hazard_description: e.target.value })}
                    rows={3}
                    placeholder="Describe the hazards associated with this step..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Existing Controls
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={jsaControlInput}
                      onChange={(e) => setJsaControlInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addJSAControl())}
                      placeholder="Enter control measure"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={addJSAControl}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {jsaStepForm.existing_controls.length > 0 && (
                    <div className="space-y-2">
                      {jsaStepForm.existing_controls.map((ctrl, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{ctrl}</span>
                          <button
                            onClick={() => removeJSAControl(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-700">
                    <strong>JSA Tip:</strong> Break down the job into logical steps. For each step, identify all potential hazards 
                    and the controls needed to mitigate them.
                  </p>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowJSAModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveJSAStep}
                  disabled={!jsaStepForm.step_description || !jsaStepForm.hazard_description}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {editingJSAStep ? 'Update Step' : 'Save Step'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CAPA Modal */}
        {showCAPAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Create CAPA from HIRA</h2>
              <p className="text-sm text-gray-600 mb-4">Link a corrective/preventive action to a hazard in this risk assessment</p>

              <div className="space-y-4">
                {/* Hazard Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Hazard <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={capaForm.hazard_id}
                    onChange={(e) => setCAPAForm({ ...capaForm, hazard_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">-- Select a hazard --</option>
                    {assessment.hazards?.map((hazard, index) => (
                      <option key={hazard.id || index} value={hazard.id || String(index)}>
                        {hazard.hazard_description} ({hazard.risk_level})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Corrective Action</label>
                  <textarea
                    value={capaForm.corrective_action}
                    onChange={(e) => setCAPAForm({ ...capaForm, corrective_action: e.target.value })}
                    rows={3}
                    placeholder="Describe the corrective action to address the hazard..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preventive Action</label>
                  <textarea
                    value={capaForm.preventive_action}
                    onChange={(e) => setCAPAForm({ ...capaForm, preventive_action: e.target.value })}
                    rows={3}
                    placeholder="Describe the preventive action to avoid recurrence..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Responsible Person</label>
                  <input
                    type="text"
                    value={capaForm.responsible_person}
                    onChange={(e) => setCAPAForm({ ...capaForm, responsible_person: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={capaForm.due_date}
                    onChange={(e) => setCAPAForm({ ...capaForm, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCAPAModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCAPA}
                  disabled={!capaForm.hazard_id || !capaForm.due_date}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Create CAPA
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default HIRADetail;
